import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Icon } from './Icon.tsx';
import { vendorTypesApi } from '../features/vendorTypes/api.ts';
import { t } from '../lib/strings.ts';
import { useConfirm } from './ConfirmDialog.tsx';

export function ManageTypes(): JSX.Element {
  const qc = useQueryClient();
  const { confirm, dialog } = useConfirm();

  const { data: types = [] } = useQuery({
    queryKey: ['vendor-types'],
    queryFn: () => vendorTypesApi.list(),
  });

  const [addName, setAddName] = useState('');
  const [addError, setAddError] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editError, setEditError] = useState<string | null>(null);
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    const name = addName.trim();
    if (!name) return;
    setAddError(null);
    setIsAdding(true);
    try {
      await vendorTypesApi.create(name);
      setAddName('');
      void qc.invalidateQueries({ queryKey: ['vendor-types'] });
    } catch (err) {
      const data = (err as { response?: { data?: { error?: string } } })?.response?.data;
      if (data?.error === 'type_exists') {
        setAddError(t.manageTypes.errorTypeExists);
      } else {
        setAddError(t.manageTypes.errorAdd);
      }
    } finally {
      setIsAdding(false);
    }
  }

  function startEdit(id: string, name: string) {
    setEditingId(id);
    setEditName(name);
    setEditError(null);
  }

  function cancelEdit() {
    setEditingId(null);
    setEditName('');
    setEditError(null);
  }

  async function handleRename(id: string) {
    const name = editName.trim();
    if (!name) return;
    setEditError(null);
    setIsSavingEdit(true);
    try {
      await vendorTypesApi.rename(id, name);
      setEditingId(null);
      void qc.invalidateQueries({ queryKey: ['vendor-types'] });
    } catch (err) {
      const data = (err as { response?: { data?: { error?: string } } })?.response?.data;
      if (data?.error === 'type_exists') {
        setEditError(t.manageTypes.errorTypeExistsRename);
      } else {
        setEditError(t.manageTypes.errorRename);
      }
    } finally {
      setIsSavingEdit(false);
    }
  }

  async function handleDelete(id: string) {
    if (!(await confirm({ body: t.manageTypes.deleteConfirm }))) return;
    try {
      await vendorTypesApi.remove(id);
      void qc.invalidateQueries({ queryKey: ['vendor-types'] });
    } catch {
      // silent — deletion may fail if in use
    }
  }

  const CHIP = 'grid size-9 shrink-0 place-items-center rounded-full bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))]';
  const ROW_DIVIDE = 'border-b border-black/[0.05] last:border-b-0 dark:border-white/[0.06]';

  return (
    /* Spans the page like the vendors tab beside it. The row chrome — chip,
       dividers, hover, quiet icon buttons — is what gives the list structure;
       narrowing the panel to pull names next to their buttons just left half
       the page empty. */
    <section className="w-full space-y-3">
      {dialog}
      <div className="flex items-center gap-2">
        <h2 className="status-label">{t.manageTypes.heading}</h2>
        {types.length > 0 && (
          <span className="num tabular text-xs text-muted-foreground">{types.length}</span>
        )}
      </div>

      <div className="overflow-hidden rounded-3xl bg-[hsl(var(--chrome))]">
        {types.length === 0 ? (
          <div className="space-y-3 p-10 text-center">
            <div className={`${CHIP} mx-auto size-12`}><Icon name="sell" size={22} /></div>
            <p className="text-sm text-muted-foreground">{t.manageTypes.empty}</p>
          </div>
        ) : (
          <ul>
            {types.map((vt) => (
              <li key={vt.id} className={ROW_DIVIDE}>
                {editingId === vt.id ? (
                  <div className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <span className={CHIP}><Icon name="sell" size={18} /></span>
                      <input
                        className="input min-w-0 flex-1 rounded-full text-sm"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); void handleRename(vt.id); }
                          if (e.key === 'Escape') cancelEdit();
                        }}
                        autoFocus
                      />
                      <div className="flex shrink-0 items-center gap-1">
                        <button
                          type="button"
                          onClick={() => void handleRename(vt.id)}
                          disabled={isSavingEdit}
                          className="btn-primary btn-icon"
                          title={t.manageTypes.ariaSaveRename}
                          aria-label={t.manageTypes.ariaSaveRename}
                        >
                          <Icon name="check" size={18} />
                        </button>
                        <button
                          type="button"
                          onClick={cancelEdit}
                          className="btn-ghost btn-icon"
                          title={t.manageTypes.ariaCancelRename}
                          aria-label={t.manageTypes.ariaCancelRename}
                        >
                          <Icon name="close" size={18} />
                        </button>
                      </div>
                    </div>
                    {editError && (
                      <p className="mt-2 ps-12 text-xs text-destructive" role="alert">{editError}</p>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-[hsl(var(--surface-high))]">
                    <span className={CHIP}><Icon name="sell" size={18} /></span>
                    <span className="min-w-0 flex-1 truncate text-sm font-medium">{vt.name}</span>
                    <div className="flex shrink-0 items-center gap-1">
                      <button
                        type="button"
                        onClick={() => startEdit(vt.id, vt.name)}
                        className="btn-secondary btn-icon"
                        title={t.manageTypes.ariaRename}
                        aria-label={t.manageTypes.ariaRename}
                      >
                        <Icon name="edit" size={18} />
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleDelete(vt.id)}
                        className="btn-ghost btn-ghost-danger btn-icon"
                        title={t.manageTypes.ariaDelete}
                        aria-label={t.manageTypes.ariaDelete}
                      >
                        <Icon name="delete" size={18} />
                      </button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}

        {/* Adding belongs to the list, so it lives in the same panel as its last row. */}
        <form
          className="border-t border-black/[0.05] p-3 dark:border-white/[0.06] sm:px-4"
          onSubmit={(e) => { void handleAdd(e); }}
        >
          <div className="flex items-center gap-2">
            <input
              className="input min-w-0 flex-1 rounded-full text-sm"
              placeholder={t.manageTypes.addPlaceholder}
              value={addName}
              onChange={(e) => { setAddName(e.target.value); setAddError(null); }}
            />
            <button type="submit" disabled={isAdding || !addName.trim()} className="btn-primary btn-sm shrink-0">
              {isAdding ? t.manageTypes.adding : t.manageTypes.addType}
            </button>
          </div>
          {addError && (
            <p className="mt-2 text-xs text-destructive" role="alert">{addError}</p>
          )}
        </form>
      </div>
    </section>
  );
}
